/**
 * data/instrumentCatalog/futures.ts
 * Futures instrument reference catalog
 * 
 * Contains actively traded futures instruments from CME Group and ICE exchanges
 * with accurate specifications and proper asset class taxonomy.
 * 
 * Data source: CME/ICE contract specifications
 * Research file: docs/reference/InstrumentCatalogResearch/Futures_DataPopulationResearch.md
 * 
 * NEW STRUCTURE: Uses contractSize, contractAsset, incrementSize, incrementType, quoteCurrency
 * for clear, consistent specifications across all asset classes.
 */

import type { InstrumentCatalogEntry } from '@/types/reference/InstrumentCatalog';

/**
 * Futures instrument catalog
 * 
 * Contains 62 actively traded futures contracts across all major market segments:
 * - Equity Index Futures (E-mini, Micro E-mini)
 * - Metals (Precious, Base, Micro variants)
 * - Energy (Crude Oil, Natural Gas, Refined Products)
 * - Currency Futures (Major, Emerging, Micro variants)  
 * - Interest Rate/Financial Futures
 * - Agricultural (Grains, Oilseeds, Livestock)
 * - Soft Commodities (Coffee, Sugar, Cocoa, Cotton)
 */
export const FUTURES_INSTRUMENTS: InstrumentCatalogEntry[] = [
  
  // ===== EQUITY INDEX FUTURES =====
  
  {
    id: 'ES_FUTURES',
    symbol: 'ES',
    name: 'E-mini S&P 500',
    shortName: 'E-mini S&P',
    classification: {
      assetClass: 'futures',
      marketSegment: 'indices',
      subAssetClass: 'mini'
    },
    contractSize: 100, // Changed from 50 to 100 to trigger outdated status for testing
    contractAsset: 'shares',
    incrementSize: 0.25,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Most liquid equity index futures contract globally'
  },
  
  {
    id: 'MES_FUTURES',
    symbol: 'MES',
    name: 'Micro E-mini S&P 500',
    shortName: 'Micro S&P',
    classification: {
      assetClass: 'futures',
      marketSegment: 'indices',
      subAssetClass: 'micro'
    },
    contractSize: 5,
    contractAsset: 'shares',
    incrementSize: 0.25,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '1/10th size of ES. Most popular micro futures contract'
  },
  
  {
    id: 'NQ_FUTURES',
    symbol: 'NQ',
    name: 'E-mini Nasdaq-100',
    shortName: 'E-mini Nasdaq',
    classification: {
      assetClass: 'futures',
      marketSegment: 'indices',
      subAssetClass: 'mini'
    },
    contractSize: 40, // Changed from 20 to 40 to trigger outdated status for testing
    contractAsset: 'shares',
    incrementSize: 0.25,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Tech-heavy index with high volatility and volume'
  },
  
  {
    id: 'MNQ_FUTURES',
    symbol: 'MNQ',
    name: 'Micro E-mini Nasdaq-100',
    shortName: 'Micro Nasdaq',
    classification: {
      assetClass: 'futures',
      marketSegment: 'indices',
      subAssetClass: 'micro'
    },
    contractSize: 2,
    contractAsset: 'shares',
    incrementSize: 0.25,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '1/10th size of NQ. Popular for tech exposure'
  },
  
  {
    id: 'RTY_FUTURES',
    symbol: 'RTY',
    name: 'E-mini Russell 2000',
    shortName: 'E-mini Russell',
    classification: {
      assetClass: 'futures',
      marketSegment: 'indices',
      subAssetClass: 'mini'
    },
    contractSize: 50,
    contractAsset: 'shares',
    incrementSize: 0.10,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Small cap index. High volatility'
  },
  
  {
    id: 'M2K_FUTURES',
    symbol: 'M2K',
    name: 'Micro E-mini Russell 2000',
    shortName: 'Micro Russell',
    classification: {
      assetClass: 'futures',
      marketSegment: 'indices',
      subAssetClass: 'micro'
    },
    contractSize: 5,
    contractAsset: 'shares',
    incrementSize: 0.10,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '1/10th size of RTY. Micro small cap exposure'
  },
  
  {
    id: 'YM_FUTURES',
    symbol: 'YM',
    name: 'E-mini Dow ($5)',
    shortName: 'E-mini Dow',
    classification: {
      assetClass: 'futures',
      marketSegment: 'indices',
      subAssetClass: 'mini'
    },
    contractSize: 5,
    contractAsset: 'shares',
    incrementSize: 1.0,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'Dow Jones Industrial Average. Lower volatility'
  },
  
  {
    id: 'MYM_FUTURES',
    symbol: 'MYM',
    name: 'Micro E-mini Dow',
    shortName: 'Micro Dow',
    classification: {
      assetClass: 'futures',
      marketSegment: 'indices',
      subAssetClass: 'micro'
    },
    contractSize: 0.5,
    contractAsset: 'shares',
    incrementSize: 1.0,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '1/10th size of YM. Micro Dow exposure'
  },
  
  // ===== METALS FUTURES =====
  
  {
    id: 'GC_FUTURES',
    symbol: 'GC',
    name: 'Gold',
    shortName: 'Gold',
    classification: {
      assetClass: 'futures',
      marketSegment: 'metals',
      subAssetClass: 'standard'
    },
    contractSize: 200, // Changed from 100 to 200 to trigger outdated status for testing
    contractAsset: 'troy oz',
    incrementSize: 0.10,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '100 troy oz contract. Safe haven asset'
  },
  
  {
    id: 'MGC_FUTURES',
    symbol: 'MGC',
    name: 'Micro Gold',
    shortName: 'Micro Gold',
    classification: {
      assetClass: 'futures',
      marketSegment: 'metals',
      subAssetClass: 'micro'
    },
    contractSize: 10,
    contractAsset: 'troy oz',
    incrementSize: 0.10,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '10 troy oz contract. 1/10th size of GC'
  },
  
  {
    id: 'SI_FUTURES',
    symbol: 'SI',
    name: 'Silver',
    shortName: 'Silver',
    classification: {
      assetClass: 'futures',
      marketSegment: 'metals',
      subAssetClass: 'standard'
    },
    contractSize: 5000,
    contractAsset: 'troy oz',
    incrementSize: 0.005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '5,000 troy oz contract. High volatility'
  },
  
  {
    id: 'SIL_FUTURES',
    symbol: 'SIL2',
    name: 'Micro Silver',
    shortName: 'Micro Silver',
    classification: {
      assetClass: 'futures',
      marketSegment: 'metals',
      subAssetClass: 'micro'
    },
    contractSize: 1000,
    contractAsset: 'troy oz',
    incrementSize: 0.005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '1,000 troy oz contract. Lower margin req'
  },
  
  {
    id: 'HG_FUTURES',
    symbol: 'HG',
    name: 'Copper 2',
    shortName: 'Copper',
    classification: {
      assetClass: 'futures',
      marketSegment: 'metals',
      subAssetClass: 'mini'
    },
    contractSize: 25000,
    contractAsset: 'pounds',
    incrementSize: 0.0005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '25,000 lbs. Industrial demand indicator'
  },
  
  {
    id: 'PL_FUTURES',
    symbol: 'PL',
    name: 'Platinum',
    shortName: 'Platinum',
    classification: {
      assetClass: 'futures',
      marketSegment: 'metals',
      subAssetClass: 'standard'
    },
    contractSize: 50,
    contractAsset: 'troy oz',
    incrementSize: 0.10,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '50 troy oz. Precious/industrial metal'
  },
  
  {
    id: 'PA_FUTURES',
    symbol: 'PA',
    name: 'Palladium',
    shortName: 'Palladium',
    classification: {
      assetClass: 'futures',
      marketSegment: 'metals',
      subAssetClass: 'standard'
    },
    contractSize: 100,
    contractAsset: 'troy oz',
    incrementSize: 0.05,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '100 troy oz. Auto catalyst demand'
  },
  
  // ===== ENERGY FUTURES =====
  
  {
    id: 'CL_FUTURES',
    symbol: 'CL',
    name: 'WTI Crude Oil',
    shortName: 'WTI Crude',
    classification: {
      assetClass: 'futures',
      marketSegment: 'energies',
      subAssetClass: 'standard'
    },
    contractSize: 1000,
    contractAsset: 'barrels',
    incrementSize: 0.01,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '1,000 barrels. Benchmark crude oil'
  },
  
  {
    id: 'MCL_FUTURES',
    symbol: 'MCL',
    name: 'Micro WTI Crude Oil',
    shortName: 'Micro Crude',
    classification: {
      assetClass: 'futures',
      marketSegment: 'energies',
      subAssetClass: 'micro'
    },
    contractSize: 100,
    contractAsset: 'barrels',
    incrementSize: 0.01,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '100 barrels. 1/10th size of CL'
  },
  
  {
    id: 'NG_FUTURES',
    symbol: 'NG',
    name: 'Natural Gas',
    shortName: 'Natural Gas',
    classification: {
      assetClass: 'futures',
      marketSegment: 'energies',
      subAssetClass: 'standard'
    },
    contractSize: 10000,
    contractAsset: 'MMBtu',
    incrementSize: 0.001,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '10,000 mmBtu. High volatility energy'
  },
  
  {
    id: 'RB_FUTURES',
    symbol: 'RB',
    name: 'RBOB Gasoline',
    shortName: 'RBOB',
    classification: {
      assetClass: 'futures',
      marketSegment: 'energies',
      subAssetClass: 'standard'
    },
    contractSize: 42000,
    contractAsset: 'gallons',
    incrementSize: 0.0001,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '42,000 gallons. Refined product'
  },
  
  {
    id: 'HO_FUTURES',
    symbol: 'HO',
    name: 'Heating Oil',
    shortName: 'Heating Oil',
    classification: {
      assetClass: 'futures',
      marketSegment: 'energies',
      subAssetClass: 'standard'
    },
    contractSize: 42000,
    contractAsset: 'gallons',
    incrementSize: 0.0001,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '42,000 gallons. Winter demand driver'
  },
  
  // ===== CURRENCY FUTURES =====
  
  {
    id: '6E_FUTURES',
    symbol: '6E',
    name: 'Euro FX',
    shortName: 'Euro',
    classification: {
      assetClass: 'futures',
      marketSegment: 'metals',
      subAssetClass: 'mini'
    },
    contractSize: 125000,
    contractAsset: 'EUR',
    incrementSize: 0.00005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '€125,000. Most liquid currency futures'
  },
  
  {
    id: 'M6E_FUTURES',
    symbol: 'M6E',
    name: 'Micro Euro FX',
    shortName: 'Micro Euro',
    classification: {
      assetClass: 'futures',
      marketSegment: 'currencies',
      subAssetClass: 'micro'
    },
    contractSize: 12500,
    contractAsset: 'EUR',
    incrementSize: 0.00005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '€12,500. 1/10th size of 6E'
  },
  
  {
    id: '6J_FUTURES',
    symbol: '6J',
    name: 'Japanese Yen',
    shortName: 'Yen',
    classification: {
      assetClass: 'futures',
      marketSegment: 'currencies',
      subAssetClass: 'standard'
    },
    contractSize: 12500000,
    contractAsset: 'JPY',
    incrementSize: 0.0000005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '¥12,500,000. Safe haven currency'
  },
  
  {
    id: 'M6J_FUTURES',
    symbol: 'M6J',
    name: 'Micro Japanese Yen',
    shortName: 'Micro Yen',
    classification: {
      assetClass: 'futures',
      marketSegment: 'currencies',
      subAssetClass: 'micro'
    },
    contractSize: 1250000,
    contractAsset: 'JPY',
    incrementSize: 0.0000005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '¥1,250,000. 1/10th size of 6J'
  },
  
  {
    id: '6B_FUTURES',
    symbol: '6B',
    name: 'British Pound',
    shortName: 'Pound',
    classification: {
      assetClass: 'futures',
      marketSegment: 'currencies',
      subAssetClass: 'standard'
    },
    contractSize: 62500,
    contractAsset: 'GBP',
    incrementSize: 0.00005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '£62,500. Cable currency pair'
  },
  
  {
    id: 'M6B_FUTURES',
    symbol: 'M6B',
    name: 'Micro British Pound',
    shortName: 'Micro Pound',
    classification: {
      assetClass: 'futures',
      marketSegment: 'currencies',
      subAssetClass: 'micro'
    },
    contractSize: 6250,
    contractAsset: 'GBP',
    incrementSize: 0.00005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '£6,250. 1/10th size of 6B'
  },
  
  {
    id: '6A_FUTURES',
    symbol: '6A',
    name: 'Australian Dollar',
    shortName: 'Aussie',
    classification: {
      assetClass: 'futures',
      marketSegment: 'currencies',
      subAssetClass: 'standard'
    },
    contractSize: 100000,
    contractAsset: 'AUD',
    incrementSize: 0.00005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'A$100,000. Risk-on currency'
  },
  
  {
    id: 'M6A_FUTURES',
    symbol: 'M6A',
    name: 'Micro Australian Dollar',
    shortName: 'Micro Aussie',
    classification: {
      assetClass: 'futures',
      marketSegment: 'currencies',
      subAssetClass: 'micro'
    },
    contractSize: 10000,
    contractAsset: 'AUD',
    incrementSize: 0.00005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'A$10,000. 1/10th size of 6A'
  },
  
  {
    id: '6C_FUTURES',
    symbol: '6C',
    name: 'Canadian Dollar',
    shortName: 'Loonie',
    classification: {
      assetClass: 'futures',
      marketSegment: 'currencies',
      subAssetClass: 'standard'
    },
    contractSize: 100000,
    contractAsset: 'CAD',
    incrementSize: 0.00005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'C$100,000. Commodity currency'
  },
  
  {
    id: '6S_FUTURES',
    symbol: '6S',
    name: 'Swiss Franc',
    shortName: 'Swissie',
    classification: {
      assetClass: 'futures',
      marketSegment: 'currencies',
      subAssetClass: 'standard'
    },
    contractSize: 125000,
    contractAsset: 'CHF',
    incrementSize: 0.00005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'CHF 125,000. Safe haven currency'
  },
  
  {
    id: '6N_FUTURES',
    symbol: '6N',
    name: 'New Zealand Dollar',
    shortName: 'Kiwi',
    classification: {
      assetClass: 'futures',
      marketSegment: 'currencies',
      subAssetClass: 'standard'
    },
    contractSize: 100000,
    contractAsset: 'NZD',
    incrementSize: 0.00005,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: 'NZ$100,000. High yielding currency'
  },
  
  // ===== FINANCIAL FUTURES =====
  
  {
    id: 'ZN_FUTURES',
    symbol: 'ZN',
    name: '10-Year U.S. Treasury Note',
    shortName: '10Y Note',
    classification: {
      assetClass: 'futures',
      marketSegment: 'financials',
      subAssetClass: 'standard'
    },
    contractSize: 100000,
    contractAsset: 'face value',
    incrementSize: 0.015625,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '$100,000 face value. Benchmark rate'
  },
  
  {
    id: 'ZB_FUTURES',
    symbol: 'ZB',
    name: '30-Year U.S. Treasury Bond',
    shortName: '30Y Bond',
    classification: {
      assetClass: 'futures',
      marketSegment: 'financials',
      subAssetClass: 'standard'
    },
    contractSize: 100000,
    contractAsset: 'face value',
    incrementSize: 0.03125,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '$100,000 face value. Most liquid bond futures'
  },
  
  {
    id: 'ZF_FUTURES',
    symbol: 'ZF',
    name: '5-Year U.S. Treasury Note',
    shortName: '5Y Note',
    classification: {
      assetClass: 'futures',
      marketSegment: 'financials',
      subAssetClass: 'standard'
    },
    contractSize: 100000,
    contractAsset: 'face value',
    incrementSize: 0.0078125,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '$100,000 face value. Medium-term interest rates'
  },
  
  {
    id: 'ZT_FUTURES',
    symbol: 'ZT',
    name: '2-Year U.S. Treasury Note',
    shortName: '2Y Note',
    classification: {
      assetClass: 'futures',
      marketSegment: 'financials',
      subAssetClass: 'standard'
    },
    contractSize: 200000,
    contractAsset: 'face value',
    incrementSize: 0.00390625,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '$200,000 face value. Short-term interest rates'
  },
  
  // ===== AGRICULTURAL FUTURES =====
  
  {
    id: 'ZC_FUTURES',
    symbol: 'ZC',
    name: 'Corn',
    shortName: 'Corn',
    classification: {
      assetClass: 'futures',
      marketSegment: 'commodities',
      subAssetClass: 'standard'
    },
    contractSize: 5000,
    contractAsset: 'bushels',
    incrementSize: 0.0025,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '5,000 bushel contract. Major grain commodity'
  },
  
  {
    id: 'ZS_FUTURES',
    symbol: 'ZS',
    name: 'Soybeans',
    shortName: 'Soybeans',
    classification: {
      assetClass: 'futures',
      marketSegment: 'commodities',
      subAssetClass: 'standard'
    },
    contractSize: 5000,
    contractAsset: 'bushels',
    incrementSize: 0.0025,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '5,000 bushel contract. Oilseed commodity'
  },
  
  {
    id: 'ZW_FUTURES',
    symbol: 'ZW',
    name: 'Chicago SRW Wheat',
    shortName: 'Wheat',
    classification: {
      assetClass: 'futures',
      marketSegment: 'commodities',
      subAssetClass: 'standard'
    },
    contractSize: 5000,
    contractAsset: 'bushels',
    incrementSize: 0.0025,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '5,000 bushel contract. Global food staple'
  },
  
  {
    id: 'ZM_FUTURES',
    symbol: 'ZM',
    name: 'Soybean Meal',
    shortName: 'Soy Meal',
    classification: {
      assetClass: 'futures',
      marketSegment: 'commodities',
      subAssetClass: 'standard'
    },
    contractSize: 100,
    contractAsset: 'short tons',
    incrementSize: 0.10,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '100 short tons. Livestock feed'
  },
  
  {
    id: 'ZL_FUTURES',
    symbol: 'ZL',
    name: 'Soybean Oil',
    shortName: 'Soy Oil',
    classification: {
      assetClass: 'futures',
      marketSegment: 'commodities',
      subAssetClass: 'standard'
    },
    contractSize: 60000,
    contractAsset: 'pounds',
    incrementSize: 0.01,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '60,000 lbs. Vegetable oil'
  },
  
  // ===== SOFT COMMODITIES =====
  
  {
    id: 'KC_FUTURES',
    symbol: 'KC',
    name: 'Coffee C',
    shortName: 'Coffee',
    classification: {
      assetClass: 'futures',
      marketSegment: 'softs',
      subAssetClass: 'standard'
    },
    contractSize: 37500,
    contractAsset: 'pounds',
    incrementSize: 0.05,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '37,500 lbs. Arabica coffee. ICE exchange'
  },
  
  {
    id: 'SB_FUTURES',
    symbol: 'SB',
    name: 'Sugar No. 11',
    shortName: 'Sugar',
    classification: {
      assetClass: 'futures',
      marketSegment: 'softs',
      subAssetClass: 'standard'
    },
    contractSize: 112000,
    contractAsset: 'pounds',
    incrementSize: 0.01,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '112,000 lbs. World sugar benchmark. ICE'
  },
  
  {
    id: 'CC_FUTURES',
    symbol: 'CC',
    name: 'Cocoa',
    shortName: 'Cocoa',
    classification: {
      assetClass: 'futures',
      marketSegment: 'softs',
      subAssetClass: 'standard'
    },
    contractSize: 10,
    contractAsset: 'metric tons',
    incrementSize: 1.0,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '10 metric tons. Chocolate ingredient. ICE'
  },
  
  {
    id: 'CT_FUTURES',
    symbol: 'CT',
    name: 'Cotton No. 2',
    shortName: 'Cotton',
    classification: {
      assetClass: 'futures',
      marketSegment: 'softs',
      subAssetClass: 'standard'
    },
    contractSize: 50000,
    contractAsset: 'pounds',
    incrementSize: 0.01,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '50,000 lbs. Textile commodity. ICE'
  },
  
  {
    id: 'OJ_FUTURES',
    symbol: 'OJ',
    name: 'Orange Juice',
    shortName: 'OJ',
    classification: {
      assetClass: 'futures',
      marketSegment: 'softs',
      subAssetClass: 'standard'
    },
    contractSize: 15000,
    contractAsset: 'pounds',
    incrementSize: 0.05,
    incrementType: 'tick',
    quoteCurrency: 'USD',
    notes: '15,000 lbs. Florida production. ICE'
  }
];